import { useState } from 'react'
import type { OrderDTO } from '@wf/shared'
import { ApiError } from '../api/client'
import { useCustomer, useCustomersList } from '../customers/useCustomers'
import { usePatchOrder } from './useOrders'

/**
 * Auftrag nachträglich einem (anderen) Kunden + dessen Fahrzeug zuordnen — z. B. wenn er ohne Kunde
 * angelegt wurde oder beim Anlegen der falsche gewählt war. Kunde → Fahrzeug hängen abhängig zusammen
 * (wie im Anlage-Wizard): ein Kundenwechsel setzt das Fahrzeug zurück, weil das alte zum alten Kunden
 * gehört. Gespeichert wird per OCC-`patchOrder` (version Pflicht) und nur bei echter Änderung; vom Server
 * übersprungene Felder (`skipped[]`) und ein 409-Konflikt werden ehrlich angezeigt.
 */
export function OrderLinkEdit({ order }: { order: OrderDTO }) {
  const patch = usePatchOrder(order.id)
  const customers = useCustomersList()
  const [customerId, setCustomerId] = useState<number | null>(order.customerId)
  const [vehicleId, setVehicleId] = useState<number | null>(order.vehicleId)
  const customerDetail = useCustomer(customerId)
  const vehicles = customerId != null ? (customerDetail.data?.vehicles ?? []) : []

  const dirty = customerId !== order.customerId || vehicleId !== order.vehicleId
  const skipped = patch.data?.skipped ?? []
  const conflict = patch.error instanceof ApiError && patch.error.status === 409 ? patch.error.message : null
  const otherError = patch.error instanceof ApiError && patch.error.status !== 409 ? patch.error.message : null

  function save() {
    if (!dirty) return
    patch.mutate({
      version: order.version,
      ...(customerId !== order.customerId ? { customerId } : {}),
      ...(vehicleId !== order.vehicleId ? { vehicleId } : {}),
    })
  }

  return (
    <section className="wf-orderedit">
      <span className="wf-label wf-sectiontitle">Kunde &amp; Fahrzeug</span>
      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Kunde</span>
          <select
            className="wf-input"
            value={customerId ?? ''}
            onChange={(event) => {
              setCustomerId(event.target.value ? Number(event.target.value) : null)
              setVehicleId(null)
            }}
          >
            <option value="">— kein Kunde —</option>
            {(customers.data?.customers ?? []).map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </select>
        </label>
        <label className="wf-field">
          <span className="wf-label">Fahrzeug</span>
          <select
            className="wf-input"
            value={vehicleId ?? ''}
            disabled={customerId == null || vehicles.length === 0}
            onChange={(event) => setVehicleId(event.target.value ? Number(event.target.value) : null)}
          >
            <option value="">— kein Fahrzeug —</option>
            {vehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.plate}
                {vehicle.model ? ` · ${vehicle.model}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="wf-orderedit__actions">
        {/* Eindeutiger Accessible-Name: auf dem Detail stehen parallel weitere „Speichern"-Knöpfe. */}
        <button type="button" className="wf-btn wf-btn--ghost" aria-label="Zuordnung speichern" disabled={!dirty || patch.isPending} onClick={save}>
          Speichern
        </button>
      </div>

      {skipped.length > 0 ? (
        <p className="warn" role="status">
          Nicht übernommen: {skipped.join(', ')} — Recht fehlt oder nach Rechnung/Freigabe gesperrt.
        </p>
      ) : null}
      {conflict ? (
        <p className="warn" role="alert">
          Konflikt: {conflict} – der aktuelle Stand wurde neu geladen.
        </p>
      ) : null}
      {otherError ? (
        <p className="error" role="alert">
          {otherError}
        </p>
      ) : null}
    </section>
  )
}
